import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LuBadgeCheck, LuCircleAlert, LuClock, LuCircleX, LuShieldOff, LuUserX, LuX, LuZoomIn } from 'react-icons/lu';
import { employeeCardUrl, getEmployeeVerification } from '../services/publicApi';

const states = {
  NotFound: {
    icon: LuUserX,
    title: 'EMPLOYEE NOT FOUND',
    message: 'We could not find an employee record for this ID.',
    tone: 'text-slate-500 bg-slate-100 dark:bg-white/10 dark:text-gray-300',
  },
  NotVerified: {
    icon: LuCircleAlert,
    title: 'NOT VERIFIED',
    message: 'This employee ID has not been verified by RAADSO TECH.',
    tone: 'text-red-600 bg-red-100 dark:bg-red-500/15 dark:text-red-400',
  },
  Pending: {
    icon: LuClock,
    title: 'VERIFICATION PENDING',
    message: 'Verification for this employee is still being reviewed.',
    tone: 'text-amber-600 bg-amber-100 dark:bg-amber-500/15 dark:text-amber-400',
  },
  Rejected: {
    icon: LuCircleX,
    title: 'VERIFICATION REJECTED',
    message: 'This employee ID could not be verified.',
    tone: 'text-red-600 bg-red-100 dark:bg-red-500/15 dark:text-red-400',
  },
  Revoked: {
    icon: LuShieldOff,
    title: 'VERIFICATION REVOKED',
    message: 'The verification for this employee has been revoked.',
    tone: 'text-red-600 bg-red-100 dark:bg-red-500/15 dark:text-red-400',
  },
  Inactive: {
    icon: LuUserX,
    title: 'NO LONGER ACTIVE',
    message: 'This person is no longer an active RAADSO TECH employee.',
    tone: 'text-red-600 bg-red-100 dark:bg-red-500/15 dark:text-red-400',
  },
};

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, { dateStyle: 'long' });
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-3 last:border-0 dark:border-white/10">
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-right text-sm font-bold text-black dark:text-white">{value}</dd>
    </div>
  );
}

const VerifyEmployee = () => {
  const { employeeId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    getEmployeeVerification(employeeId)
      .then((result) => active && setData(result))
      .catch((err) => active && setError(err.message || 'Could not load verification.'))
      .finally(() => active && setLoading(false));
    document.title = `Verify ${employeeId} | RAADSO TECH`;
    return () => {
      active = false;
    };
  }, [employeeId]);

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (event) => event.key === 'Escape' && setZoom(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom]);

  const isVerified = data?.state === 'Verified';
  const info = data && !isVerified ? states[data.state] || states.NotVerified : null;
  const cards = isVerified
    ? [
        { label: 'Front of ID card', src: employeeCardUrl(data.frontCardUrl) },
        { label: 'Back of ID card', src: employeeCardUrl(data.backCardUrl) },
      ]
    : [];

  return (
    <div className="min-h-screen bg-gray-50 text-black dark:bg-black dark:text-white">
      <header className="border-b border-gray-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-[#0b0b0b]">
        <div className="mx-auto flex max-w-xl items-center justify-between">
          <Link to="/" aria-label="RAADSO TECH home">
            <img src="/img/logo-black.png" alt="RAADSO TECH" className="h-10 object-contain dark:hidden" />
            <img src="/img/logo-white.png" alt="RAADSO TECH" className="hidden h-10 object-contain dark:block" />
          </Link>
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Employee verification</span>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-8">
        {loading ? (
          <div className="rounded-3xl bg-white p-10 text-center text-sm text-gray-500 shadow-sm dark:bg-[#101010] dark:text-gray-400" role="status">
            Verifying {employeeId}…
          </div>
        ) : null}

        {error ? (
          <div className="rounded-3xl bg-white p-8 text-center shadow-sm dark:bg-[#101010]" role="alert">
            <p className="text-lg font-bold">Could not verify right now</p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-black"
            >
              Try again
            </button>
          </div>
        ) : null}

        {info ? (
          <div className="rounded-3xl bg-white p-8 text-center shadow-sm dark:bg-[#101010]">
            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full ${info.tone}`}>
              <info.icon size={40} />
            </div>
            <h1 className="mt-5 text-2xl font-extrabold tracking-wide">{info.title}</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{info.message}</p>
            <p className="mt-4 text-xs text-gray-400">ID: {data.employeeId}</p>
          </div>
        ) : null}

        {isVerified ? (
          <div className="space-y-6">
            <div className="rounded-3xl bg-white p-8 text-center shadow-sm dark:bg-[#101010]">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-400">
                <LuBadgeCheck size={52} />
              </div>
              <h1 className="mt-5 text-3xl font-extrabold tracking-wide text-green-600 dark:text-green-400">VERIFIED</h1>
              <p className="mt-1 text-base font-semibold">This employee is one of us.</p>

              <dl className="mt-6 text-left">
                <Detail label="Full name" value={data.fullName} />
                <Detail label="Employee ID" value={data.employeeId} />
                <Detail label="Position" value={data.position} />
                <Detail label="Department" value={data.department} />
                <Detail label="Employed since" value={formatDate(data.employmentDate)} />
                <Detail label="Verified on" value={formatDate(data.verifiedAt)} />
              </dl>
            </div>

            <div className="rounded-3xl bg-white p-6 shadow-sm dark:bg-[#101010]">
              <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Official ID card</h2>
              <div className="space-y-4">
                {cards.map((card) => (
                  <button
                    key={card.label}
                    type="button"
                    onClick={() => setZoom(card)}
                    className="group relative block w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-black"
                    aria-label={`View ${card.label} larger`}
                  >
                    <img src={card.src} alt={card.label} loading="lazy" className="w-full object-contain" />
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-white">
                      <LuZoomIn size={14} /> Enlarge
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <p className="mt-8 text-center text-xs text-gray-400">© {new Date().getFullYear()} RAADSO TECH · raadsotech.com</p>
      </main>

      {zoom ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={zoom.label}
          onClick={() => setZoom(null)}
        >
          <button
            type="button"
            onClick={() => setZoom(null)}
            className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white"
            aria-label="Close"
          >
            <LuX size={24} />
          </button>
          <img src={zoom.src} alt={zoom.label} className="max-h-full max-w-full object-contain" onClick={(event) => event.stopPropagation()} />
        </div>
      ) : null}
    </div>
  );
};

export default VerifyEmployee;
